import 'reflect-metadata';

import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';

export interface MultipartUploadCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
}

export interface MultipartUploadOptions {
  bucket: string;
  key: string;
  file: File | Blob;
  contentType?: string;
  region: string;
  credentials: MultipartUploadCredentials;
  queueSize?: number; // default 4
  partSizeMB?: number; // default 8
  onProgress?: (info: {
    loaded: number;
    total: number;
    percent: number;
  }) => void;
  client?: S3Client; // optional injection (tests)
}

export interface MultipartUploadController {
  abort: () => void;
  promise: Promise<void>;
}

export function performMultipartUpload(
  options: MultipartUploadOptions,
): MultipartUploadController {
  const {
    bucket,
    key,
    file,
    contentType,
    region,
    credentials,
    queueSize = 4,
    partSizeMB = 8,
    onProgress,
    client: injectedClient,
  } = options;

  const client =
    injectedClient ||
    new S3Client({
      region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
      useAccelerateEndpoint: true,
    });

  const hasChecksumAlgorithm = (
    v: unknown,
  ): v is { ChecksumAlgorithm?: unknown } =>
    !!v && typeof v === 'object' && 'ChecksumAlgorithm' in v;

  client.middlewareStack.add(
    next => async (args: { input?: unknown }) => {
      try {
        if (hasChecksumAlgorithm(args.input)) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete args.input.ChecksumAlgorithm;
        }
      } catch {
        /* ignore */
      }
      // @ts-expect-error forwarding unknown
      return next(args);
    },
    { step: 'initialize', name: 'stripChecksumAlgorithmInput' },
  );

  const upload = new Upload({
    client,
    params: { Bucket: bucket, Key: key, Body: file, ContentType: contentType },
    queueSize,
    partSize: partSizeMB * 1024 * 1024,
  });

  upload.on('httpUploadProgress', (p: { loaded?: number; total?: number }) => {
    if (!onProgress) return;
    if (!p.total) return;
    const loaded = p.loaded || 0;
    const total = p.total;
    const percent = Math.min(100, Math.round((loaded / total) * 100));
    onProgress({ loaded, total, percent });
  });

  const promise = upload.done().then(() => void 0);

  return {
    abort: () => {
      try {
        upload.abort();
      } catch {
        /* noop */
      }
    },
    promise,
  };
}
